import { FC } from 'react';

import Link from 'next/link';

import { useMediaQuery } from '@mui/material';

import { Link as Scroll } from 'react-scroll';
import { useRecoilValue } from 'recoil';

import { usePagePathParams } from '@hooks_common/usePagePathParams';

import SectionLeadLeftAlign from '@components_iqfu/public/section_lead_left_align/SectionLeadLeftAlign';

import publicHeaderHeight from '@recoil_iqfu/atoms/public/publicHeaderHeight';

import { MansionDetailData } from '@type-def_iqfu/public/MansionDetailData';
import { SalesStatsData } from '@type-def_iqfu/public/MarketData';

import { MediaQuery } from '@constants_common';

import styles from './MansionGeneralInfo.module.scss';

type Props = {
  displayData: SalesStatsData;
  areaInfo: { country: string; prefecture?: string; city?: string; parent_major_city?: string; city_category?: string };
};

const MansionGeneralInfo: FC<Props> = ({ displayData, areaInfo }) => {
  const isMobile = useMediaQuery(MediaQuery.lg);

  const pathParam = usePagePathParams();
  const mediaLinkQueryParam = `?utm_source=iqfu&utm_medium=referral&utm_campaign=mansion_general_info&utm_content=${pathParam}`;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.section_lead_left_container}>
          <SectionLeadLeftAlign>
            {displayAreaName(displayData.average_sale_price_this_year?.area_type, areaInfo, true)}の売却相場・動向の概要
          </SectionLeadLeftAlign>
        </div>
        <div className={styles.desktop_stacks}>
          <MansionSellingPeriodCard displayData={displayData} areaInfo={areaInfo} />
          <MansionSellingPriceCard displayData={displayData} areaInfo={areaInfo} />
          <MansionHighPriceFeatures displayData={displayData} areaInfo={areaInfo} />
        </div>

        {displayData.property_features?.media_url.value ? (
          <div className={styles.media_link_container}>
            <Link
              href={`${displayData.property_features.media_url.value}${mediaLinkQueryParam}`}
              prefetch={false}
              target={isMobile ? '_self' : '_blank'}
              className={`${styles.tips_media_link}`}
            >
              {displayAreaName(displayData.property_features.media_url.area_type, areaInfo, true)}の中古マンション
              <br />
              売却価格相場・動向
            </Link>
          </div>
        ) : (
          <></>
        )}
      </div>
    </>
  );
};

const displayAreaName = (
  areaType: string,
  areaInfo: { country: string; prefecture?: string; city?: string; parent_major_city?: string; city_category?: string },
  cityNameWithoutPrefectureName: boolean = false,
) => {
  switch (areaType) {
    case 'country':
      return areaInfo.country ?? '全国';
    case 'prefecture':
      return areaInfo.prefecture ?? '';
    case 'city':
      if (cityNameWithoutPrefectureName) return areaInfo.city ?? '';
      return (areaInfo.prefecture ?? '') + (areaInfo.city ?? '');
    case 'major_city':
      return areaInfo.city_category === 'major' ? areaInfo.city ?? '' : areaInfo.parent_major_city ?? '';
    default:
      return '';
  }
};

export const MansionSellingPeriodCard: FC<Props> = ({ displayData, areaInfo }) => {
  return (
    <>
      {displayData.average_sale_period_by_month?.value && (
        <div className={styles.info_block}>
          <h3 className={styles.block_lead}>
            {displayAreaName(displayData.average_sale_period_by_month.area_type, areaInfo)}のマンションの
            <br />
            現在の平均売却期間
          </h3>
          <div className={styles.block_mainData}>
            <strong className={styles.data_text}>
              <span className={styles.data_number}>{displayData.average_sale_period_by_month.value}</span>
              ヶ月
            </strong>
          </div>
        </div>
      )}
    </>
  );
};

export const MansionSellingPriceCard: FC<Props> = ({ displayData, areaInfo }) => {
  return (
    <>
      {displayData.average_sale_price_this_year?.value && (
        <div className={styles.info_block}>
          <h3 className={styles.block_lead}>
            {displayAreaName(displayData.average_sale_price_this_year.area_type, areaInfo)}のマンションの
            <br />
            現在の平均売却価格
          </h3>
          {displayData.average_sale_price_this_year.value && (
            <div className={styles.block_mainData}>
              <strong className={styles.data_text}>
                <span className={styles.data_number}>
                  {displayData.average_sale_price_this_year.value.toLocaleString()}
                </span>
                万円
              </strong>
              {/* TODO 間取りは後日確認 */}
              <p className={styles.data_remarks}>（3LDKの間取り）</p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export const MansionHighPriceFeatures: FC<Props> = ({ displayData, areaInfo }) => {
  return (
    <>
      {displayData.property_features?.feature_first.value && (
        <div className={styles.high_price_features}>
          <h3 className={styles.high_price_features_lead}>
            今、
            {displayData.property_features.feature_first.area_type === 'country'
              ? '全国で'
              : `「${displayAreaName(displayData.property_features.feature_first.area_type, areaInfo)}」で`}
            高く売れるマンションの特徴
          </h3>
          <ul className={styles.high_price_features_list}>
            {displayData.property_features.feature_first.value && (
              <li className={styles.high_price_features_item}>
                <span className={styles.item_circle}></span>
                <p className={styles.item_text}>{displayData.property_features.feature_first.value}</p>
              </li>
            )}
            {displayData.property_features.feature_second.value && (
              <li className={styles.high_price_features_item}>
                <span className={styles.item_circle}></span>
                <div className={styles.item_text}>
                  <p className={styles.item_text}>{displayData.property_features.feature_second.value}</p>
                </div>
              </li>
            )}
            {displayData.property_features.feature_third.value && (
              <li className={styles.high_price_features_item}>
                <span className={styles.item_circle}></span>
                <div className={styles.item_text}>
                  <p className={styles.item_text}>{displayData.property_features.feature_third.value}</p>
                </div>
              </li>
            )}
          </ul>
        </div>
      )}
    </>
  );
};

export const MansionDetailPriceHeading: FC<{
  displayMansionData: MansionDetailData;
  hasSaleCases: boolean;
}> = ({ displayMansionData, hasSaleCases }) => {
  const headerHeight = useRecoilValue(publicHeaderHeight);

  return (
    <div className={styles.mansion_detail_price_heading_wrap}>
      {hasSaleCases ? (
        <>
          {displayMansionData.average_sale_price ? (
            <div>
              <h2 className={styles.mansion_detail_price_heading_lead}>
                「{displayMansionData.name}」 実際の売却価格相場
              </h2>
              <div>
                <Scroll to="mansion_detail_sale_case_area" smooth={true} offset={-(headerHeight + 4)}>
                  <div className={styles.mansion_detail_price_heading_price}>
                    約
                    <span className={styles.price_number}>
                      {displayMansionData.average_sale_price.toLocaleString()}
                    </span>
                    万円
                  </div>
                </Scroll>
              </div>
              <div>
                <Scroll to="mansion_detail_sale_case_area" smooth={true} offset={-(headerHeight + 4)}>
                  <div className={styles.mansion_detail_price_text_link}>実際に売れたマンション売却実績を見る</div>
                </Scroll>
              </div>
            </div>
          ) : (
            <>
              <p className={styles.mansion_detail_lead_text}>{displayMansionData.name}の最近の売却実績がありません</p>
              <Scroll to="mansion_detail_sale_case_area" smooth={true} offset={-(headerHeight + 4)}>
                <span className={styles.mansion_detail_sale_case_link}>
                  <p>近隣のマンション売却実績を見る</p>
                </span>
              </Scroll>
            </>
          )}
        </>
      ) : (
        <p className={styles.mansion_detail_lead_text}>{displayMansionData.name}の最近の売却実績がありません</p>
      )}
    </div>
  );
};

export default MansionGeneralInfo;
